import { useMemo, useState, useEffect } from 'react';
import { musicPlayer } from '../musicPlayer.js';
import { generateMusicalNotes } from '../musicAlgorithms/index.js';

const PLAYBACK_SPEED_CALIBRATION = 1.0;

const MusicNotationView = ({
  pitch,
  intensity,
  pitch1stDerivative,
  segment,
  broadcastTime,
  showWordLabels = false
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackProgress, setPlaybackProgress] = useState(0);
  const [playbackError, setPlaybackError] = useState(null);
  const [playbackSpeed, setPlaybackSpeed] = useState(1.0);
  const [volume, setVolume] = useState(1.2);
  const [isMobile, setIsMobile] = useState(false);
  const [activePlaybackNoteIndex, setActivePlaybackNoteIndex] = useState(null);

  const totalSegmentDuration = segment ? segment.end - segment.start : 0;
  const words = segment?.words || [];

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    musicPlayer.setVolume(volume, true);
  }, [volume]);

  const musicalNotes = useMemo(() => {
    return generateMusicalNotes(
      pitch,
      intensity,
      segment,
      pitch1stDerivative,
      broadcastTime
    );
  }, [pitch, intensity, segment, broadcastTime, pitch1stDerivative]);

  useEffect(() => {
    return () => {
      musicPlayer.destroy();
    };
  }, []);

  const playableNotes = useMemo(() => {
    if (!musicalNotes || !Array.isArray(musicalNotes)) return [];
    return musicalNotes
      .filter(
        (note) =>
          note &&
          note.type !== 'rest' &&
          note.musicalNote &&
          note.musicalNote !== 'REST' &&
          Number.isFinite(note.startTime) &&
          Number.isFinite(note.endTime)
      )
      .map((note, index) => ({
        ...note,
        playbackIndex: index
      }));
  }, [musicalNotes]);

  const playableNotesCount = playableNotes.length;

  const noteTimeline = useMemo(() => {
    if (!playableNotes.length) {
      return { start: null, end: null, duration: 0 };
    }

    const start = Math.min(...playableNotes.map((n) => n.startTime));
    const end = Math.max(...playableNotes.map((n) => n.endTime));

    return {
      start,
      end,
      duration: Math.max(0, end - start)
    };
  }, [playableNotes]);

  const effectivePlaybackSpeed = playbackSpeed * PLAYBACK_SPEED_CALIBRATION;

  const estimatedPlaybackTime =
    noteTimeline.duration > 0
      ? noteTimeline.duration / Math.max(effectivePlaybackSpeed, 0.01)
      : 0;

  const absoluteBroadcastTime = useMemo(() => {
    if (!Number.isFinite(broadcastTime) || !segment) return null;
    return segment.start + broadcastTime;
  }, [broadcastTime, segment]);

  const activeReferenceTime = absoluteBroadcastTime;

  const activePlaybackNote = useMemo(() => {
    if (!isPlaying || activePlaybackNoteIndex == null) return null;
    return playableNotes.find((note) => note.playbackIndex === activePlaybackNoteIndex) || null;
  }, [isPlaying, activePlaybackNoteIndex, playableNotes]);

  const staffCursorRelativeTime = useMemo(() => {
    if (isPlaying && activePlaybackNote && segment) {
      return activePlaybackNote.time - segment.start;
    }

    if (Number.isFinite(broadcastTime)) {
      return broadcastTime;
    }

    return null;
  }, [isPlaying, activePlaybackNote, segment, broadcastTime]);

  const isNoteActive = (note) => {
    if (!note) return false;

    if (isPlaying) {
      return note.playbackIndex === activePlaybackNoteIndex;
    }

    if (!Number.isFinite(activeReferenceTime)) return false;
    if (!Number.isFinite(note.startTime) || !Number.isFinite(note.endTime)) return false;

    return activeReferenceTime >= note.startTime && activeReferenceTime <= note.endTime;
  };

  const activeNotes = playableNotes.filter((note) => isNoteActive(note));

  const handlePlay = async () => {
    if (musicPlayer.isPlaying) {
      const stopResult = await musicPlayer.stopPlayback();
      const progressValue =
        typeof stopResult === 'number'
          ? stopResult
          : (stopResult?.progress ?? 0);

      const normalizedProgress =
        progressValue <= 1 ? progressValue * 100 : progressValue;

      setPlaybackProgress(Math.max(0, Math.min(100, normalizedProgress)));
      setIsPlaying(false);
      setActivePlaybackNoteIndex(null);
      return;
    }

    if (!musicalNotes || musicalNotes.length === 0 || !playableNotes.length) {
      setPlaybackError('No notes available to play');
      return;
    }

    musicPlayer.setPlaybackSpeed(effectivePlaybackSpeed);
    musicPlayer.setVolume(volume);

    setIsPlaying(true);
    setPlaybackError(null);
    setPlaybackProgress(0);
    setActivePlaybackNoteIndex(null);

    try {
      await musicPlayer.playNotes(musicalNotes, (payload) => {
        const rawProgress =
          typeof payload === 'number'
            ? payload
            : (payload?.progress ?? 0);

        const activeNoteIndex =
          typeof payload === 'object'
            ? payload?.activeNoteIndex ?? null
            : null;

        const normalizedProgress =
          rawProgress <= 1 ? rawProgress * 100 : rawProgress;

        const clampedProgress = Math.max(0, Math.min(100, normalizedProgress));
        setPlaybackProgress(clampedProgress);
        setActivePlaybackNoteIndex(activeNoteIndex);

        if (clampedProgress >= 99.9) {
          setIsPlaying(false);
          setActivePlaybackNoteIndex(null);
        }
      });
    } catch (error) {
      setPlaybackError(`Playback failed: ${error.message}`);
      setIsPlaying(false);
      setPlaybackProgress(0);
      setActivePlaybackNoteIndex(null);
    }
  };

  const handleSpeedChange = (speed) => {
    setPlaybackSpeed(speed);
    if (musicPlayer.isPlaying) {
      musicPlayer.setPlaybackSpeed(speed * PLAYBACK_SPEED_CALIBRATION);
    }
  };

  const handleVolumeChange = (newVolume) => {
    setVolume(newVolume);
    musicPlayer.setVolume(newVolume, true);
  };

  const shouldShowWordLabels =
    !isMobile && showWordLabels && words?.length > 0 && words?.length < 10;

  if (!pitch?.xs || pitch.xs.length === 0) {
    return (
      <div className="bg-white p-4 rounded-xl border border-gray-200 text-center text-gray-500 shadow-sm">
        <div className="text-lg mb-2">🎵</div>
        No pitch data available for music notation
      </div>
    );
  }

  if (isMobile) {
    return (
      <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
        <div className="flex flex-col gap-3 mb-4">
          <h3 className="text-lg font-bold text-gray-900 text-center">Musical Notes</h3>

          <div className="flex justify-center items-center gap-3 text-xs text-gray-600 bg-gray-50 px-3 py-2 rounded-lg">
            <span className="font-semibold">{playableNotesCount} notes</span>
            <span>•</span>
            <span className="font-semibold">{estimatedPlaybackTime.toFixed(1)}s</span>
            {activeNotes.length > 0 && (
              <>
                <span>•</span>
                <span className="text-green-600 font-semibold">
                  Playing: {activeNotes[0]?.musicalNote}
                </span>
              </>
            )}
          </div>

          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-2 bg-gray-50 px-3 py-2 rounded-lg border border-gray-300">
              <span className="text-xs text-gray-600 font-medium">Volume</span>
              <div className="flex items-center gap-2 flex-1 max-w-32">
                <input
                  type="range"
                  min="0"
                  max="3"
                  step="0.1"
                  value={volume}
                  onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                  className="w-full accent-blue-500 cursor-pointer"
                />
                <span className="text-xs text-gray-600 font-medium w-8">
                  {Math.round(volume * 100)}%
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-gray-600 font-medium">Speed</span>
              <div className="flex items-center gap-1 border border-gray-300 rounded-lg p-1 bg-gray-50 flex-1 max-w-48">
                {[0.25, 0.5, 1.0].map((speed) => (
                  <button
                    key={speed}
                    onClick={() => handleSpeedChange(speed)}
                    className={`flex-1 px-2 py-1 text-xs rounded-md transition-all font-medium ${
                      playbackSpeed === speed
                        ? 'bg-blue-500 text-white shadow-sm'
                        : 'bg-white text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {speed === 0.25 ? '25%' : speed === 0.5 ? '50%' : '100%'}
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={handlePlay}
              disabled={playableNotesCount === 0 || !musicalNotes}
              className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all duration-200 shadow-sm ${
                isPlaying
                  ? 'bg-red-500 hover:bg-red-600 text-white'
                  : 'bg-green-500 hover:bg-green-600 text-white'
              } ${playableNotesCount === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {isPlaying ? (
                <>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Stop
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Play
                </>
              )}
            </button>
          </div>
        </div>

        {playbackError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="text-xs text-red-800 font-medium text-center flex items-center gap-2 justify-center">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
              {playbackError}
            </div>
          </div>
        )}

        {isPlaying && (
          <div className="mb-4">
            <div className="w-full bg-gray-100 rounded-full h-2 shadow-inner">
              <div
                className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all duration-100"
                style={{ width: `${playbackProgress}%` }}
              />
            </div>
            <div className="text-xs text-gray-600 mt-1 text-center">
              {playbackSpeed * 100}% speed • {Math.round(playbackProgress)}% complete
            </div>
          </div>
        )}

        <MobileStaff
          musicalNotes={playableNotes}
          cursorRelativeTime={staffCursorRelativeTime}
          totalSegmentDuration={totalSegmentDuration}
          isNoteActive={isNoteActive}
        />
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <h3 className="text-xl font-bold text-gray-900">Musical Notation</h3>

          <div className="flex items-center gap-4 text-sm text-gray-600">
            <div className="bg-blue-50 px-3 py-1 rounded-full border border-blue-100">
              <span className="font-semibold text-blue-700">{playableNotesCount}</span>
              <span className="text-blue-600 ml-1">notes</span>
            </div>
            <div className="bg-green-50 px-3 py-1 rounded-full border border-green-100">
              <span className="font-semibold text-green-700">
                {estimatedPlaybackTime.toFixed(1)}s
              </span>
              <span className="text-green-600 ml-1">duration</span>
            </div>
            {activeNotes.length > 0 && (
              <div className="bg-emerald-50 px-3 py-1 rounded-full border border-emerald-100">
                <span className="font-semibold text-emerald-700">Playing:</span>
                <span className="text-emerald-600 ml-1">{activeNotes[0]?.musicalNote}</span>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3 bg-gray-50 px-4 py-2 rounded-lg border border-gray-200">
            <svg className="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.415z"
                clipRule="evenodd"
              />
            </svg>
            <input
              type="range"
              min="0"
              max="3"
              step="0.1"
              value={volume}
              onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
              className="w-24 accent-blue-500 cursor-pointer"
            />
            <span className="text-sm text-gray-600 font-medium w-12">
              {Math.round(volume * 100)}%
            </span>
          </div>

          <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg border border-gray-200">
            <span className="text-sm text-gray-600 font-medium whitespace-nowrap">Speed:</span>
            <div className="flex items-center gap-1">
              {[0.25, 0.5, 0.75, 1.0, 1.5, 2.0].map((speed) => (
                <button
                  key={speed}
                  onClick={() => handleSpeedChange(speed)}
                  className={`px-3 py-1 text-sm rounded-md transition-all font-medium border ${
                    playbackSpeed === speed
                      ? 'bg-blue-500 text-white border-blue-600 shadow-sm'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {speed}x
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={handlePlay}
            disabled={playableNotesCount === 0 || !musicalNotes}
            className={`flex items-center justify-center gap-3 px-6 py-3 rounded-xl font-semibold transition-all duration-200 shadow-sm ${
              isPlaying
                ? 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white'
                : 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white'
            } ${playableNotesCount === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {isPlaying ? (
              <>
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                Stop Playback
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                    clipRule="evenodd"
                  />
                </svg>
                Play Sequence
              </>
            )}
          </button>
        </div>
      </div>

      {playbackError && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-3 text-sm text-red-800 font-medium">
            <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
            {playbackError}
          </div>
        </div>
      )}

      {isPlaying && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Playback Progress</span>
            <span className="text-sm text-gray-600">
              {playbackSpeed}x speed • {Math.round(playbackProgress)}% complete
            </span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-3 shadow-inner">
            <div
              className="bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 h-3 rounded-full transition-all duration-100 shadow-sm"
              style={{ width: `${playbackProgress}%` }}
            />
          </div>
        </div>
      )}

      <PCStaff
        musicalNotes={playableNotes}
        cursorRelativeTime={staffCursorRelativeTime}
        totalSegmentDuration={totalSegmentDuration}
        isNoteActive={isNoteActive}
      />

      {shouldShowWordLabels && (
        <PCWordTimeline
          words={words}
          segment={segment}
          totalSegmentDuration={totalSegmentDuration}
          broadcastTime={broadcastTime}
        />
      )}
    </div>
  );
};

const MobileStaff = ({ musicalNotes, cursorRelativeTime, totalSegmentDuration, isNoteActive }) => (
  <div className="mb-4">
    <div className="relative h-40 bg-white rounded-lg border border-gray-300 overflow-visible">
      <div className="absolute top-0 left-0 right-0 h-2/5 border-b border-gray-300">
        {[0, 1, 2, 3, 4].map((line) => (
          <div
            key={`treble-line-${line}`}
            className="absolute left-0 right-0 border-t border-gray-600"
            style={{ top: `${line * 25}%` }}
          />
        ))}
        <div className="absolute left-2 top-1/2 transform -translate-y-1/2 text-lg font-serif text-gray-900">
          𝄞
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-2/5 border-t border-gray-300">
        {[0, 1, 2, 3, 4].map((line) => (
          <div
            key={`bass-line-${line}`}
            className="absolute left-0 right-0 border-t border-gray-600"
            style={{ top: `${line * 25}%` }}
          />
        ))}
        <div className="absolute left-2 top-1/2 transform -translate-y-1/2 text-lg font-serif text-gray-900">
          𝄢
        </div>
      </div>

      {Number.isFinite(cursorRelativeTime) && totalSegmentDuration > 0 && (
        <div
          className="absolute top-0 bottom-0 w-0.5 bg-green-500 z-10"
          style={{ left: `${(cursorRelativeTime / totalSegmentDuration) * 100}%` }}
        />
      )}

      {musicalNotes?.map((note, index) => {
        if (note.type === 'rest' || note.musicalNote === 'REST') return null;

        let topPosition;
        if (note.staff === 'treble' && (note.musicalNote === 'E4' || note.musicalNote === 'D4')) {
          topPosition = note.musicalNote === 'E4' ? '37.5%' : '41.25%';
        } else {
          topPosition = note.staff === 'treble'
            ? `${10 + note.linePosition * 12}%`
            : `${60 + note.linePosition * 12}%`;
        }

        const isActive = isNoteActive(note);
        const labelOffset = note.stemDirection === 'up' ? 'bottom-8' : 'top-8';

        return (
          <div
            key={`mobile-note-${index}`}
            className={`absolute flex flex-col items-center transition-all duration-300 z-20 ${
              isActive ? 'scale-110' : 'scale-100'
            }`}
            style={{ left: `${note.position}%`, top: topPosition }}
          >
            {note.stemHeight > 0 && (
              <div
                className={`absolute z-10 ${
                  isActive ? 'w-1 bg-green-600' : 'w-0.5 bg-gray-800'
                }`}
                style={{
                  height: `${note.stemHeight * (isActive ? 1.2 : 1)}px`,
                  left: note.stemDirection === 'up' ? '70%' : '30%',
                  top: note.stemDirection === 'up' ? '0%' : '100%',
                  transform: note.stemDirection === 'up' ? 'translateY(-100%)' : 'translateY(0)'
                }}
              />
            )}

            <div
              className={`relative z-20 transition-all duration-200 ${
                note.noteType === 'whole' || note.noteType === 'half'
                  ? 'border rounded-full bg-transparent'
                  : 'rounded-full'
              } ${
                isActive
                  ? 'bg-green-500 border-green-600 shadow-md'
                  : note.noteType === 'whole' || note.noteType === 'half'
                    ? 'border-black'
                    : 'bg-black'
              }`}
              style={{
                width: isActive ? '1rem' : '0.875rem',
                height: isActive ? '1rem' : '0.875rem'
              }}
            >
              {note.noteType === 'eighth' && note.stemDirection === 'up' && (
                <div
                  className={`absolute border-t border-r transform rotate-45 ${
                    isActive
                      ? '-right-1 -top-3 w-3 h-3 border-green-600'
                      : '-right-0.5 -top-2 w-2 h-2 border-black'
                  }`}
                />
              )}
              {note.noteType === 'eighth' && note.stemDirection === 'down' && (
                <div
                  className={`absolute border-b border-r transform -rotate-45 ${
                    isActive
                      ? '-right-1 -bottom-3 w-3 h-3 border-green-600'
                      : '-right-0.5 -bottom-2 w-2 h-2 border-black'
                  }`}
                />
              )}
            </div>

            {isActive && (
              <div
                className={`absolute ${labelOffset} text-xs font-bold whitespace-nowrap px-1.5 py-0.5 rounded-md border shadow-sm backdrop-blur-sm z-30 ${
                  isActive
                    ? 'bg-green-100 text-green-800 border-green-300'
                    : 'bg-white/95 text-gray-900 border-gray-200'
                }`}
              >
                {note.musicalNote}
              </div>
            )}
          </div>
        );
      })}
    </div>
  </div>
);

const PCStaff = ({ musicalNotes, cursorRelativeTime, totalSegmentDuration, isNoteActive }) => (
  <div className="mb-6">
    <div className="relative h-48 bg-gradient-to-b from-gray-50 to-white rounded-lg border border-gray-300 shadow-inner overflow-visible">
      <div className="absolute top-0 left-0 right-0 h-2/5 border-b border-gray-300 bg-gradient-to-b from-blue-50/30 to-transparent">
        {[0, 1, 2, 3, 4].map((line) => (
          <div
            key={`pc-treble-line-${line}`}
            className="absolute left-0 right-0 border-t border-gray-600"
            style={{ top: `${line * 25}%` }}
          />
        ))}
        <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-2xl font-serif text-gray-900">
          𝄞
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-2/5 border-t border-gray-300 bg-gradient-to-t from-green-50/30 to-transparent">
        {[0, 1, 2, 3, 4].map((line) => (
          <div
            key={`pc-bass-line-${line}`}
            className="absolute left-0 right-0 border-t border-gray-600"
            style={{ top: `${line * 25}%` }}
          />
        ))}
        <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-2xl font-serif text-gray-900">
          𝄢
        </div>
      </div>

      {Number.isFinite(cursorRelativeTime) && totalSegmentDuration > 0 && (
        <div
          className="absolute top-0 bottom-0 w-0.5 bg-gradient-to-b from-green-500 to-green-600 z-10 shadow-lg"
          style={{ left: `${(cursorRelativeTime / totalSegmentDuration) * 100}%` }}
        />
      )}

      {musicalNotes?.map((note, index) => {
        if (note.type === 'rest' || note.musicalNote === 'REST') return null;

        let topPosition;
        if (note.staff === 'treble' && (note.musicalNote === 'E4' || note.musicalNote === 'D4')) {
          topPosition = note.musicalNote === 'E4' ? '37.5%' : '41.25%';
        } else {
          topPosition = note.staff === 'treble'
            ? `${10 + note.linePosition * 12}%`
            : `${60 + note.linePosition * 12}%`;
        }

        const isActive = isNoteActive(note);
        const labelOffset = note.stemDirection === 'up' ? 'bottom-10' : 'top-10';

        return (
          <div
            key={`pc-note-${index}`}
            className={`absolute flex flex-col items-center transition-all duration-300 z-20 ${
              isActive ? 'scale-125' : 'scale-100'
            }`}
            style={{ left: `${note.position}%`, top: topPosition }}
          >
            {note.stemHeight > 0 && (
              <div
                className={`absolute z-10 ${
                  isActive ? 'w-1.5 bg-gradient-to-b from-green-600 to-green-700' : 'w-1 bg-gray-800'
                }`}
                style={{
                  height: `${note.stemHeight * (isActive ? 1.3 : 1)}px`,
                  left: note.stemDirection === 'up' ? '70%' : '30%',
                  top: note.stemDirection === 'up' ? '0%' : '100%',
                  transform: note.stemDirection === 'up' ? 'translateY(-100%)' : 'translateY(0)'
                }}
              />
            )}

            <div
              className={`relative z-20 transition-all duration-200 ${
                note.noteType === 'whole' || note.noteType === 'half'
                  ? 'border-2 rounded-full bg-transparent'
                  : 'rounded-full'
              } ${
                isActive
                  ? 'bg-gradient-to-br from-green-400 to-green-600 border-green-700 shadow-lg'
                  : note.noteType === 'whole' || note.noteType === 'half'
                    ? 'border-black'
                    : 'bg-black'
              }`}
              style={{
                width: isActive ? '1.25rem' : '1rem',
                height: isActive ? '1.25rem' : '1rem'
              }}
            >
              {note.noteType === 'eighth' && note.stemDirection === 'up' && (
                <div
                  className={`absolute border-t-2 border-r-2 transform rotate-45 ${
                    isActive
                      ? '-right-1.5 -top-4 w-4 h-4 border-green-700'
                      : '-right-1 -top-3 w-3 h-3 border-black'
                  }`}
                />
              )}
              {note.noteType === 'eighth' && note.stemDirection === 'down' && (
                <div
                  className={`absolute border-b-2 border-r-2 transform -rotate-45 ${
                    isActive
                      ? '-right-1.5 -bottom-4 w-4 h-4 border-green-700'
                      : '-right-1 -bottom-3 w-3 h-3 border-black'
                  }`}
                />
              )}
            </div>

            {isActive && (
              <div
                className={`absolute ${labelOffset} text-sm font-bold whitespace-nowrap px-2 py-1 rounded-lg border shadow-lg backdrop-blur-sm z-30 ${
                  isActive
                    ? 'bg-gradient-to-br from-green-100 to-green-200 text-green-900 border-green-300'
                    : 'bg-white/95 text-gray-900 border-gray-200'
                }`}
              >
                {note.musicalNote}
                <div className="text-xs font-normal text-green-700 mt-0.5">
                  {note.duration?.toFixed(2)}s
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  </div>
);

const PCWordTimeline = ({ words, segment, totalSegmentDuration, broadcastTime }) => (
  <div className="mt-6">
    <div className="flex items-center justify-between mb-3">
      <h4 className="text-lg font-semibold text-gray-900">Word Alignment</h4>
      <span className="text-sm text-gray-600">{words.length} words</span>
    </div>

    <div className="relative h-20 bg-gradient-to-b from-gray-50 to-white rounded-xl border border-gray-300 shadow-inner overflow-hidden">
      <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-r from-gray-100 to-gray-200 border-t border-gray-300 flex items-center justify-between px-4">
        {[0, 0.25, 0.5, 0.75, 1].map((percent) => (
          <div key={`pc-scale-${percent}`} className="flex flex-col items-center">
            <div className="w-0.5 h-2 bg-gray-500"></div>
            <div className="text-xs font-medium text-gray-700 mt-1">
              {(totalSegmentDuration * percent).toFixed(1)}s
            </div>
          </div>
        ))}
      </div>

      {words.map((word, index) => {
        const wordStart = word.start - segment.start;
        const wordEnd = word.end - segment.start;
        const startPosition = (wordStart / totalSegmentDuration) * 100;
        const endPosition = (wordEnd / totalSegmentDuration) * 100;
        const width = Math.max(3, endPosition - startPosition);
        const centerPosition = startPosition + width / 2;
        const isActive =
          Number.isFinite(broadcastTime) &&
          broadcastTime >= wordStart &&
          broadcastTime <= wordEnd;

        return (
          <div key={`pc-word-${index}`}>
            <div
              className={`absolute top-2 h-12 rounded-lg border-2 transition-all duration-200 ${
                isActive
                  ? 'bg-gradient-to-br from-blue-200 to-blue-300 border-blue-400 shadow-md'
                  : 'bg-gradient-to-br from-blue-100 to-blue-200 border-blue-300'
              }`}
              style={{
                left: `${startPosition}%`,
                width: `${width}%`
              }}
            />

            {width > 6 && (
              <div
                className={`absolute top-4 transform -translate-x-1/2 text-sm font-semibold px-3 py-1 rounded-lg border shadow-lg transition-all duration-200 backdrop-blur-sm max-w-32 text-center truncate ${
                  isActive
                    ? 'bg-white text-blue-800 border-blue-400 scale-105'
                    : 'bg-white/90 text-gray-800 border-gray-300'
                }`}
                style={{ left: `${centerPosition}%` }}
              >
                {word.text}
                {isActive && (
                  <div className="text-xs font-normal text-blue-600 mt-0.5">
                    {((wordEnd - wordStart) * 1000).toFixed(0)}ms
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {Number.isFinite(broadcastTime) && (
        <div
          className="absolute top-0 bottom-0 w-1 bg-gradient-to-b from-green-500 to-green-600 z-20 shadow-xl rounded-full"
          style={{ left: `${(broadcastTime / totalSegmentDuration) * 100}%` }}
        >
          <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-3 h-3 bg-green-500 rounded-full border-2 border-white shadow-lg"></div>
        </div>
      )}
    </div>
  </div>
);

export default MusicNotationView;